/**
 * HTML Sanitization Utility
 * Provides XSS protection for user-generated HTML content
 */
import DOMPurify from 'dompurify';

// Configure DOMPurify with safe defaults
const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'em', 'u', 's', 'b', 'i',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'a', 'img',
  'blockquote', 'pre', 'code',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'div', 'span',
  'hr'
];

const ALLOWED_ATTR = [
  'href', 'src', 'alt', 'title', 'class', 'style',
  'target', 'rel', 'width', 'height',
  'data-testid'
];

// Forbidden URL schemes
const FORBID_ATTR = ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'];

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param {string} dirtyHtml - Potentially unsafe HTML content
 * @param {object} options - Optional DOMPurify configuration overrides
 * @returns {string} Sanitized HTML safe for dangerouslySetInnerHTML
 */
export const sanitizeHtml = (dirtyHtml, options = {}) => {
  if (!dirtyHtml || typeof dirtyHtml !== 'string') {
    return '';
  }

  const config = {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    FORBID_ATTR,
    ALLOW_DATA_ATTR: false,
    // Remove javascript: URLs
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    // Strip dangerous protocols
    ADD_ATTR: ['target'],
    // Force all links to open in new tab with security attributes
    FORCE_BODY: true,
    ...options
  };

  // Sanitize the HTML
  let cleanHtml = DOMPurify.sanitize(dirtyHtml, config);

  // Post-processing: Add rel="noopener noreferrer" to external links
  cleanHtml = cleanHtml.replace(
    /<a\s+([^>]*href="https?:\/\/[^"]*"[^>]*)>/gi,
    (match, attrs) => {
      if (!attrs.includes('rel=')) {
        return `<a ${attrs} rel="noopener noreferrer">`;
      }
      return match;
    }
  );

  return cleanHtml;
};

/**
 * Sanitize HTML for email template previews
 * More permissive but still safe
 */
export const sanitizeEmailHtml = (dirtyHtml) => {
  if (!dirtyHtml || typeof dirtyHtml !== 'string') {
    return '';
  }

  return DOMPurify.sanitize(dirtyHtml, {
    ALLOWED_TAGS: [
      ...ALLOWED_TAGS,
      'style', 'center', 'font', 'tbody', 'thead'
    ],
    ALLOWED_ATTR: [
      ...ALLOWED_ATTR,
      'bgcolor', 'cellpadding', 'cellspacing', 'border',
      'align', 'valign', 'color', 'size', 'face'
    ],
    FORBID_ATTR,
    ALLOW_DATA_ATTR: false,
    WHOLE_DOCUMENT: false
  });
};

/**
 * Strip all HTML tags, returning plain text
 * Useful for meta descriptions, notifications, etc.
 */
export const stripHtml = (html) => {
  if (!html || typeof html !== 'string') {
    return '';
  }
  return DOMPurify.sanitize(html, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
};

export default {
  sanitizeHtml,
  sanitizeEmailHtml,
  stripHtml
};
